import type { ReactNode } from "react"
import { memo, useEffect, useMemo, useState } from "react"
import { format } from "date-fns"
import { IconClockTimeOutline18 as History } from "nucleo-ui-outline-18"

import { Badge, Button, Dialog, Empty } from "@productlane/ui"

import type {
	VersionAuthorSource,
	VersionAuthorUser,
} from "@/components/version-author"
import { VersionAuthorLine } from "@/components/version-author"
import { groupVersionsByDay } from "./group-versions"

export interface VersionHistoryEntry {
	id: string
	createdAt: number
	createdBySource: VersionAuthorSource
	createdBy?: VersionAuthorUser | null
}

interface VersionHistoryDialogProps<T extends VersionHistoryEntry> {
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	// Shown after "Restore version for" in the header.
	entityName: ReactNode
	versions: readonly T[]
	renderPreview: (version: T) => ReactNode
	onRestore: (version: T) => void
	emptyText: string
	isLoading?: boolean
}

function VersionHistoryDialogInner<T extends VersionHistoryEntry>({
	isOpen,
	onOpenChange,
	entityName,
	versions,
	renderPreview,
	onRestore,
	emptyText,
	isLoading = false,
}: VersionHistoryDialogProps<T>) {
	const groups = useMemo(() => groupVersionsByDay(versions), [versions])
	const newest = groups[0]?.versions[0]
	const [selectedId, setSelectedId] = useState<string | undefined>(newest?.id)

	// A save landing while the dialog is open must not leave the preview empty.
	useEffect(() => {
		if (!selectedId || versions.some((v) => v.id === selectedId)) return
		setSelectedId(newest?.id)
	}, [versions, selectedId, newest?.id])

	useEffect(() => {
		if (isOpen) setSelectedId(newest?.id)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen])

	const selected = versions.find((v) => v.id === selectedId) ?? newest

	return (
		<Dialog
			size="xl"
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			title={
				<span className="flex items-center gap-1.5">
					<span className="font-normal text-secondary">
						Restore version for
					</span>
					{entityName}
				</span>
			}
			scrollContainerClasses="overflow-hidden p-0"
		>
			{versions.length === 0 && isLoading ? null : versions.length === 0 ? (
				<Empty
					image={<History className="h-8 w-8 text-gray-400" />}
					title="No version history"
					text={emptyText}
					className="h-[60dvh]"
				/>
			) : (
				<div className="flex h-[70dvh] min-h-0 overflow-hidden rounded-lg ring-1 ring-black/5 dark:ring-white/10">
					<aside className="w-[260px] shrink-0 overflow-y-auto border-r border-gray-200 pb-2 dark:border-gray-700/80">
						{groups.map((group) => (
							<div key={group.key} className="px-2 pt-3">
								<p className="px-2 pb-1 text-xs font-medium text-tertiary">
									{group.label}
								</p>
								{group.versions.map((version) => (
									<VersionRow
										key={version.id}
										version={version}
										isSelected={version.id === selected?.id}
										isCurrent={version.id === newest?.id}
										onSelect={() => setSelectedId(version.id)}
									/>
								))}
							</div>
						))}
					</aside>

					<div className="relative min-w-0 flex-1 overflow-y-auto">
						{selected && renderPreview(selected)}
						{selected && selected.id !== newest?.id && (
							<div className="pointer-events-none sticky bottom-0 flex justify-center pb-5">
								<Button
									variant="default"
									className="pointer-events-auto shadow-md ring-1 ring-black/5 dark:ring-white/10"
									onClick={() => onRestore(selected)}
								>
									Restore version
								</Button>
							</div>
						)}
					</div>
				</div>
			)}
		</Dialog>
	)
}

export const VersionHistoryDialog = memo(
	VersionHistoryDialogInner,
) as typeof VersionHistoryDialogInner

interface VersionRowProps<T extends VersionHistoryEntry> {
	version: T
	isSelected: boolean
	isCurrent: boolean
	onSelect: () => void
}

function VersionRow<T extends VersionHistoryEntry>({
	version,
	isSelected,
	isCurrent,
	onSelect,
}: VersionRowProps<T>) {
	return (
		<button
			type="button"
			onClick={onSelect}
			className={
				isSelected
					? "flex w-full flex-col gap-1 rounded-md bg-gray-150 px-2 py-1.5 text-left dark:bg-gray-800"
					: "flex w-full flex-col gap-1 rounded-md px-2 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-850"
			}
		>
			<span className="flex items-center justify-between gap-2">
				<span className="text-sm font-medium">
					{format(new Date(version.createdAt), "MMM d, yyyy")}{" "}
					<span className="font-normal text-secondary">
						{format(new Date(version.createdAt), "HH:mm")}
					</span>
				</span>
				{isCurrent && <Badge variant="default">Current</Badge>}
			</span>
			<VersionAuthorLine
				source={version.createdBySource}
				user={version.createdBy}
			/>
		</button>
	)
}
